import { Models } from "@/api/types";

type SignupRewardDiscountType =
  | "PercentOff"
  | "AmountOff"
  | "FreeItemSingle"
  | "FreeItemAll"
  | "FreeItemChooseFrom"
  | "InOffice";

export const getSignupRewardDiscountType = (
  activeSignupRewardData?: Models<"DisplayActiveReward"> | null
): SignupRewardDiscountType => {
  if (activeSignupRewardData?.discount?.amountOff) {
    return "AmountOff";
  }
  if (activeSignupRewardData?.discount?.percentOff) {
    return "PercentOff";
  }
  if (activeSignupRewardData?.freePackageGroup?.hasSelectableInclusions) {
    return "FreeItemChooseFrom";
  }

  if (
    activeSignupRewardData?.freePackageGroup?.freePackageGroupInclusions
      ?.length === 1
  ) {
    return "FreeItemSingle";
  }

  if (activeSignupRewardData?.freePackageGroup) {
    return "FreeItemAll";
  }

  return "InOffice";
};

export const getSignupRewardInclusions = (
  activeSignupRewardData: Models<"DisplayActiveReward"> | null,
  listings: Models<"ListingModel">[]
): Models<"ListingModel">[] => {
  if (!activeSignupRewardData) {
    return [];
  }

  const discountType = getSignupRewardDiscountType(activeSignupRewardData);

  // Case 1: discount && amount off - use requirements
  if (discountType === "AmountOff") {
    const requirements = activeSignupRewardData.discount?.discountRequirements;
    if (requirements && requirements.length > 0) {
      return listings.filter((listing) =>
        listing.packages?.some((pkg) =>
          requirements.some(
            (req: { packageId?: string | null }) => req.packageId === pkg.id
          )
        )
      );
    }
  }

  // Case 2: discount && percent off - use inclusions
  if (discountType === "PercentOff") {
    const inclusions = activeSignupRewardData?.discount?.discountInclusions;
    if (inclusions && inclusions.length > 0) {
      return listings.filter((listing) =>
        listing.packages?.some((pkg) =>
          inclusions.some(
            (inc: { packageId?: string | null }) => inc.packageId === pkg.id
          )
        )
      );
    }
  }

  // Case 3: free item - use free package group
  if (
    discountType === "FreeItemAll" ||
    discountType === "FreeItemSingle" ||
    discountType === "FreeItemChooseFrom"
  ) {
    const freePackageGroupInclusions =
      activeSignupRewardData.freePackageGroup?.freePackageGroupInclusions;
    if (freePackageGroupInclusions && freePackageGroupInclusions.length > 0) {
      return listings.filter((listing) =>
        listing.packages?.some((pkg) =>
          freePackageGroupInclusions.some(
            (inc: { packageId?: string | null }) => inc.packageId === pkg.id
          )
        )
      );
    }
  }

  return [];
};

export const getSignupRewardTitle = (
  signupReward: Models<"DisplayActiveReward">
) => {
  const signupRewardDiscountType = getSignupRewardDiscountType(signupReward);

  const amountOff = Number(signupReward.discount?.amountOff ?? 0) / 100;
  const discountPercentOff =
    Number(signupReward.discount?.percentOff ?? 0) * 100;

  if (signupRewardDiscountType === "AmountOff") {
    return `$${amountOff} off`;
  }

  if (signupRewardDiscountType === "PercentOff") {
    return `${discountPercentOff}% off`;
  }

  if (signupRewardDiscountType === "FreeItemAll") {
    return `Free items`;
  }

  if (signupRewardDiscountType === "FreeItemSingle") {
    return `A free item`;
  }

  if (signupRewardDiscountType === "FreeItemChooseFrom") {
    return `Choose from free items`;
  }

  return `A reward`;
};
